import { Link } from "react-router-dom";
import { Phone, MessageSquareText, MapPin, Mail } from "lucide-react";
import SocialIcons from "@/components/SocialIcons";
import {
  PHONE_DISPLAY,
  TEL_HREF,
  SMS_HREF,
  LOGO_DARK,
  BUSINESS_NAME,
  BUSINESS_EMAIL,
} from "@/data/site";

const SERVICES = [
  { label: "Residential Cleaning", to: "/cleaning-services" },
  { label: "Commercial Cleaning", to: "/cleaning-services" },
  { label: "Move-In/Move-Out Cleaning", to: "/cleaning-services" },
  { label: "Junk Removal", to: "/junk-removal" },
];

const COMPANY = [
  { label: "About Us", to: "/about" },
  { label: "Commercial", to: "/commercial" },
  { label: "Our Work", to: "/our-work" },
  { label: "FAQs", to: "/faqs" },
  { label: "Service Areas", to: "/service-areas" },
  { label: "Contact", to: "/free-estimate" },
];

export default function Footer() {
  return (
    <footer className="bg-ink text-white pt-16 pb-28 xl:pb-12" data-testid="site-footer">
      <div className="container-x">
        <div className="grid gap-12 lg:grid-cols-4">
          <div className="lg:pr-6">
            <Link to="/" aria-label={`${BUSINESS_NAME} — home`} className="inline-block">
              <img
                src={LOGO_DARK}
                alt="Authentically Yours Property Services logo"
                width="200"
                height="200"
                className="h-24 w-auto"
                loading="lazy"
              />
            </Link>
            <p className="mt-4 text-white/60 text-sm leading-relaxed max-w-xs">
              Locally owned. Based in Greensboro, proudly serving surrounding
              communities throughout the Triad and nearby areas.
            </p>
            <SocialIcons className="mt-6" />
          </div>

          <nav aria-label="Services">
            <h2 className="eyebrow text-blush mb-5">Services</h2>
            <ul className="space-y-3">
              {SERVICES.map((s) => (
                <li key={s.label}>
                  <Link
                    to={s.to}
                    className="text-white/75 hover:text-white text-sm transition-colors"
                  >
                    {s.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <nav aria-label="Company">
            <h2 className="eyebrow text-blush mb-5">Company</h2>
            <ul className="space-y-3">
              {COMPANY.map((s) => (
                <li key={s.label}>
                  <Link
                    to={s.to}
                    className="text-white/75 hover:text-white text-sm transition-colors"
                  >
                    {s.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div>
            <h2 className="eyebrow text-blush mb-5">Contact</h2>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center gap-2 text-white/75">
                <MapPin size={16} strokeWidth={1.5} aria-hidden="true" /> Greensboro, NC &amp; the Triad
              </li>
              <li>
                <a href={TEL_HREF} className="flex items-center gap-2 text-white/90 hover:text-blush transition-colors" data-testid="footer-call-link">
                  <Phone size={16} strokeWidth={1.5} aria-hidden="true" /> {PHONE_DISPLAY}
                </a>
              </li>
              {BUSINESS_EMAIL && (
                <li>
                  <a href={`mailto:${BUSINESS_EMAIL}`} className="flex items-center gap-2 text-white/90 hover:text-blush transition-colors" data-testid="footer-email-link">
                    <Mail size={16} strokeWidth={1.5} aria-hidden="true" /> {BUSINESS_EMAIL}
                  </a>
                </li>
              )}
              <li className="flex gap-4 pt-1">
                <a href={TEL_HREF} className="link-underline text-white/75 hover:text-white">Call</a>
                <a href={SMS_HREF} className="link-underline text-white/75 hover:text-white" data-testid="footer-text-link">Text</a>
              </li>
              <li className="pt-2">
                <Link to="/free-estimate" className="btn-primary btn-on-dark !py-3 !px-5" data-testid="footer-estimate-btn">
                  Get a Free Estimate
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="rule-gold my-10" />

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
          <p className="eyebrow text-white/70 !tracking-[0.2em]">
            Residential &amp; Commercial &middot; Insured &middot; Free Estimates
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            <Link to="/privacy" className="text-white/70 hover:text-white text-xs transition-colors" data-testid="footer-privacy">Privacy Policy</Link>
            <Link to="/terms" className="text-white/70 hover:text-white text-xs transition-colors" data-testid="footer-terms">Terms</Link>
            <p className="text-white/70 text-xs">
              © 2026 {BUSINESS_NAME}. All Rights Reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
